import { useEffect, useState } from 'react';
import { Banner, Btn, H, Hint, Icon, useInnspel } from '../../lib/index.ts';
import { hentHandoff, type HandoffResultat } from '../api.ts';
import type { PortalKonfig } from '../konfig.ts';

/**
 * 4 — TV-overlevering, nådd via QR-lenke `/h/<id>`. Konteksten fra TV-en vises
 * som fjernbare linjer (DD-05), og to valg: send anonymt (trygt på en
 * familie-TV) eller logg inn hos leietakeren. Selve overleveringen er reservert
 * v2 (kontrakten svarer 501), så flaten viser i dag «utløpt»/«uten kontekst»
 * og lar innsenderen sende likevel (m0-plan.md, M1.5).
 */
export function TvOverlevering({ id, konfig, onLoggInn, fetch: hent }: { id: string; konfig: PortalKonfig; onLoggInn: () => void; fetch?: typeof fetch }) {
  const { open } = useInnspel();
  const [res, setRes] = useState<HandoffResultat | null>(null);
  const [fjernede, setFjernede] = useState<string[]>([]);

  useEffect(() => {
    let aktiv = true;
    void hentHandoff(konfig.baseUrl, konfig.tenantKey, id, hent).then((r) => aktiv && setRes(r));
    return () => {
      aktiv = false;
    };
  }, [konfig.baseUrl, konfig.tenantKey, id, hent]);

  if (!res) {
    return <H nivaa={1} sub="Henter det TV-en sendte med …">{`Innspill til ${konfig.tenantName}`}</H>;
  }

  if (res.status !== 'ok') {
    return (
      <>
        <H nivaa={1} sub="QR-koden på TV-en gjelder i 10 minutter.">
          {res.status === 'utlopt' ? 'Denne lenken har gått ut' : 'Fikk ikke tak i det TV-en sendte med'}
        </H>
        <Banner ikon="time-outline" tone="warning" live testId="tv-utlopt">
          {res.status === 'utlopt'
            ? 'Vis en ny QR-kode på TV-en — åpne «Send innspill» i menyen der, og skann på nytt.'
            : 'Du kan sende innspillet likevel — da må du selv skrive hvilken enhet og skjerm det gjaldt.'}
        </Banner>
        <Btn variant="secondary" ikon="chatbubble-outline" testId="send-uten-tv" label="Send innspill uten TV-konteksten" hint="Da må du selv skrive hvilken enhet og skjerm det gjaldt." onClick={() => void open()} />
      </>
    );
  }

  const linjer = Object.entries(res.handoff.context)
    .filter(([k, v]) => typeof v === 'string' && v && !fjernede.includes(k))
    .map(([k, v]) => [k, ETIKETT[k] ?? k, menneskeord(k, v as string)] as const);

  return (
    <>
      <H nivaa={1} sub="Fra TV-en din. Fortsett her — det er lettere å skrive på mobilen.">{`Innspill til ${konfig.tenantName}`}</H>
      <div role="group" aria-label="Kontekst fra TV-en, hver linje kan fjernes" className="innspel-stabel" data-gap="sm">
        <div className="portal-tv-linje">
          <Icon navn="tv-outline" />
          {linjer.map(([, , v]) => v).join(' · ')}
        </div>
        <ul className="innspel-liste" style={{ gap: 0 }}>
          {linjer.map(([felt, etikett, verdi]) => (
            <li key={felt} className="innspel-kontekst-linje">
              <span className="innspel-kontekst-nokkel">{etikett}</span>
              <span className="innspel-kontekst-verdi">{verdi}</span>
              <button type="button" className="innspel-ikonknapp" aria-label={`Fjern ${etikett.toLowerCase()} fra innsendingen`} onClick={() => setFjernede((f) => [...f, felt])}>
                <Icon navn="close-outline" />Fjern
              </button>
            </li>
          ))}
        </ul>
        <Hint>Aldri: hva du så på, profilnavn, kontonummer. Lista er lukket — TV-appen kan ikke sende mer enn dette.</Hint>
      </div>
      <Btn tall ikon="chatbubble-outline" label="Send anonymt" hint="Trygt på en familie-TV: ingen konto kobles. Du kan ikke følge saken etterpå." onClick={() => void open({ screen: res.handoff.context.screen })} />
      {konfig.loginRedirectUrl ? (
        <Btn tall variant="secondary" ikon="key-outline" label={`Logg inn hos ${konfig.tenantName}`} hint={`For å følge saken. Du logger inn hos ${konfig.tenantName}, ikke her — vi får aldri navnet ditt.`} onClick={onLoggInn} />
      ) : null}
    </>
  );
}

/**
 * DD-50 presisert 2026-09-20: kontraktens enumverdi er REPOETS ord, aldri innsenderens.
 * `platform: 'tvos'` sto ordrett som enhetens navn på denne skjermen — «tvos» er ikke et
 * ord noen sier. Verdien oversettes; alt annet vises som det kom fra TV-en.
 */
const PLATTFORM: Record<string, string> = {
  tvos: 'TV',
  android: 'Android',
  ios: 'iPhone',
  web: 'Nettleser',
};

function menneskeord(felt: string, verdi: string): string {
  return felt === 'platform' ? (PLATTFORM[verdi] ?? verdi) : verdi;
}

const ETIKETT: Record<string, string> = {
  platform: 'Enhet',
  os: 'System',
  appVersion: 'Appversjon',
  build: 'Bygg',
  screen: 'Skjerm',
  locale: 'Språk',

};
